import { ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import { View as RNView, StyleSheet } from 'react-native';
import { Button } from '@shared/components/Button';
import { View } from '@shared/components/View';
import { MBTextOverlineBold } from '@shared/components/typography';
import { MBCardActionsSheet } from '@shared/scenes/cards/actions/sheets';
import { ColorThemeContext, grid } from '@shared/styles';
import { useIsDesktop, useScreenSize } from '@shared/util/hooks';
import { i18n } from '@shared/util/i18n';
import { CardActionsStateContext, CardFilterStateContext } from './context';

export function MBCardActions() {
  const isDesktop = useIsDesktop();
  const theme = useContext(ColorThemeContext);
  const [sheetActive, setSheetActive] = useState<boolean>(false);
  const { active: actionsPanelActive, setActive: setActionsPanelActive } =
    useContext(CardActionsStateContext);
  const { appliedFilters } = useContext(CardFilterStateContext);
  const screenSize = useScreenSize();
  const anchorName = '--card-actions-filter';

  const handleActivate = useCallback(() => setSheetActive(true), []);
  const handleDeactivate = useCallback(() => {
    setSheetActive(false);
    setActionsPanelActive(false);
  }, [setActionsPanelActive]);

  const filtersButton = useMemo<ReactNode>(() => {
    const title =
      isDesktop && screenSize !== 'large'
        ? ''
        : i18n('card.actions.filters.title', { count: appliedFilters.length });
    return (
      <Button
        variant="tertiary"
        icon="filter-funnel"
        label={title}
        engaged={appliedFilters.length > 0}
        onClick={handleActivate}
        style={{ height: grid(5.5) }}
      />
    );
  }, [appliedFilters, handleActivate, isDesktop, screenSize]);

  if (isDesktop) {
    return (
      <>
        <RNView style={styles.buttonRowHeader} testID="header-filter-button">
          <View style={{ anchorName }}>{filtersButton}</View>
        </RNView>
        {sheetActive && <MBCardActionsSheet onClose={handleDeactivate} anchorName={anchorName} />}
      </>
    );
  }

  if (appliedFilters.length === 0 && !(actionsPanelActive || sheetActive)) {
    return null;
  }

  return (
    <>
      <RNView style={[styles.container, { backgroundColor: theme.background }]}>
        <RNView style={styles.title}>
          <MBTextOverlineBold color={theme.foregroundInactive}>
            {i18n('card.actions.title')}
          </MBTextOverlineBold>
        </RNView>
        <RNView style={styles.buttonRow}>
          <View style={{ anchorName }}>{filtersButton}</View>
        </RNView>
      </RNView>
      {(actionsPanelActive || sheetActive) && (
        <MBCardActionsSheet onClose={handleDeactivate} anchorName={anchorName} />
      )}
    </>
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: 'column',
    padding: grid(2),
    shadowColor: 'black',
    shadowOffset: { width: 2, height: 2 },
    shadowOpacity: 0.2,
    shadowRadius: grid(1),
    elevation: grid(1),
  },
  title: {
    paddingLeft: grid(1),
    paddingBottom: grid(1),
  },
  buttonRow: {
    flexDirection: 'row',
    marginBottom: grid(2),
  },
  buttonRowHeader: {
    flexDirection: 'row',
  },
});
